import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Loader2, FileText, X } from "lucide-react";
import ClinicalOutput from "../components/ClinicalOutput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Severity → color helper ─────────────────────────────────────────────
const SEV_COLOR = {
  CRITICAL: "bg-rose-100 text-rose-800 border-rose-200",
  HIGH: "bg-orange-100 text-orange-800 border-orange-200",
  MODERATE: "bg-amber-100 text-amber-800 border-amber-200",
  LOW: "bg-slate-100 text-slate-700 border-slate-200",
};

const BUCKET_COLOR = {
  PEDS: "bg-indigo-50 text-indigo-700 border-indigo-200",
  ADULT: "bg-cyan-50 text-cyan-700 border-cyan-200",
  MULTI: "bg-violet-50 text-violet-700 border-violet-200",
};

function _bucketOf(specialty) {
  const s = (specialty || "").toUpperCase();
  if (s.includes("PED")) return "PEDS";
  if (s.includes("MULTI") || s.includes("DOMAIN")) return "MULTI";
  return "ADULT";
}

// ─── A single case row ─────────────────────────────────────────────────────
function CaseRow({ c, onOpen }) {
  const bucket = _bucketOf(c.specialty);
  return (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 rounded-md border border-slate-200 bg-white hover:border-slate-300 transition"
      data-testid={`vb-case-row-${c.case_id}`}
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Badge variant="outline" className={`shrink-0 text-[10px] font-bold border ${SEV_COLOR[c.expected_min_severity] || SEV_COLOR.LOW}`}>
          {c.expected_min_severity}
        </Badge>
        <Badge variant="outline" className={`shrink-0 text-[10px] font-bold border ${BUCKET_COLOR[bucket]}`}>
          {bucket}
        </Badge>
        <span className="truncate text-sm text-slate-800 font-medium">{c.title}</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={() => onOpen(c)}
        className="shrink-0 border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition"
        data-testid={`vb-view-soap-${c.case_id}`}
      >
        <FileText className="w-3.5 h-3.5 mr-1.5" />
        View SOAP
      </Button>
    </div>
  );
}

// ─── Modal that lazy-runs a case + displays Case Info + SOAP ─────────────
function CaseSoapDialog({ open, onClose, caseObj }) {
  const [phase, setPhase] = useState("idle"); // idle | running | done | error
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  useEffect(() => {
    if (!open || !caseObj) return;
    let cancelled = false;
    setPhase("running");
    setResult(null);
    setErrMsg(null);
    axios.post(`${API}/test-bank/run/${caseObj.case_id}`, {}, { headers: { "x-system-test": "pai-system-test-2026" } })
      .then(res => {
        if (cancelled) return;
        if (res.data?.full_result) {
          setResult(res.data);
          setPhase("done");
        } else {
          setErrMsg("Pipeline returned no result.");
          setPhase("error");
        }
      })
      .catch(err => {
        if (cancelled) return;
        setErrMsg(err.response?.data?.error || err.message || "Failed to run case.");
        setPhase("error");
      });
    return () => { cancelled = true; };
  }, [open, caseObj]);

  if (!caseObj) return null;
  const intake = result?.intake || caseObj.intake || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="max-w-4xl max-h-[92vh] overflow-y-auto p-0"
        data-testid="vb-soap-dialog"
      >
        <DialogHeader className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
          <DialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-700" />
            {caseObj.title}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Case ID: {caseObj.case_id} · Specialty: {caseObj.specialty} · Fictional case · No PHI
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-5 space-y-5">
          {/* ─── Case Information (intake the patient submitted) ─── */}
          <section data-testid="vb-case-info">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Case Information</h3>
            <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-sm grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><span className="font-semibold text-slate-600">Age</span><div className="text-slate-900">{intake.age || "—"}</div></div>
              <div><span className="font-semibold text-slate-600">Sex</span><div className="text-slate-900">{intake.sex || "—"}</div></div>
              <div><span className="font-semibold text-slate-600">Language</span><div className="text-slate-900">{intake.language || "English"}</div></div>
              <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Chief Complaint</span><div className="text-slate-900 whitespace-pre-wrap">{intake.chiefComplaint || "—"}</div></div>
              {intake.symptoms && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Symptoms</span><div className="text-slate-900 whitespace-pre-wrap">{intake.symptoms}</div></div>
              )}
              {intake.medications && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Medications</span><div className="text-slate-900 whitespace-pre-wrap">{intake.medications}</div></div>
              )}
              {intake.existingConditions && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Conditions / PMH</span><div className="text-slate-900 whitespace-pre-wrap">{intake.existingConditions}</div></div>
              )}
              {intake.notes && (
                <div className="sm:col-span-3"><span className="font-semibold text-slate-600">Notes</span><div className="text-slate-900 whitespace-pre-wrap">{intake.notes}</div></div>
              )}
            </div>
          </section>

          {/* ─── SOAP output ─── */}
          <section data-testid="vb-soap-output">
            <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Clinical Output (SOAP)</h3>
            {phase === "running" && (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-8 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />
                Running deterministic pipeline…
              </div>
            )}
            {phase === "error" && (
              <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                {errMsg || "Unable to run this case. Please close and try again."}
              </div>
            )}
            {phase === "done" && result?.full_result && (
              <ClinicalOutput result={result.full_result} viewMode="physician" />
            )}
          </section>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="vb-soap-close" size="sm">
            <X className="w-3.5 h-3.5 mr-1.5" />
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Custom Case Panel (unchanged in spirit, simplified UI) ──────────────
function CustomCasePanel() {
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("Female");
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [symptoms, setSymptoms] = useState("");
  const [medications, setMedications] = useState("");
  const [existingConditions, setExistingConditions] = useState("");
  const [notes, setNotes] = useState("");
  const [phase, setPhase] = useState("idle");
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);
  const [errMsg, setErrMsg] = useState(null);

  const submit = async () => {
    if (!age || !chiefComplaint) {
      setErrMsg("Age and Chief Complaint are required.");
      setPhase("error");
      setOpen(true);
      return;
    }
    setPhase("running"); setOpen(true); setResult(null); setErrMsg(null);
    try {
      const res = await axios.post(`${API}/intake`, {
        age, sex, language: "English",
        chiefComplaint, symptoms, medications, existingConditions, notes,
        communicationMode: "text", viewMode: "physician", consentGiven: true,
      }, { headers: { "x-system-test": "pai-system-test-2026" } });
      setResult(res.data);
      setPhase("done");
    } catch (err) {
      setErrMsg(err.response?.data?.error || err.message || "Pipeline error.");
      setPhase("error");
    }
  };

  const closeAndReset = () => { setOpen(false); setPhase("idle"); setResult(null); };

  return (
    <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-5 mb-8" data-testid="vb-custom-panel">
      <div className="flex items-center gap-2 mb-3">
        <Badge variant="outline" className="border-indigo-300 text-indigo-800 bg-indigo-50 font-bold">
          Custom Case
        </Badge>
        <span className="text-sm text-slate-600">Type any clinical scenario — see the deterministic output.</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <Input placeholder="Age" value={age} onChange={(e) => setAge(e.target.value)} data-testid="vb-cc-age" />
        <Select value={sex} onValueChange={setSex}>
          <SelectTrigger data-testid="vb-cc-sex"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Female">Female</SelectItem>
            <SelectItem value="Male">Male</SelectItem>
            <SelectItem value="Other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <Textarea placeholder="Chief complaint (required)" value={chiefComplaint} onChange={(e) => setChiefComplaint(e.target.value)} className="mb-2" data-testid="vb-cc-cc" />
      <Textarea placeholder="Symptoms (optional)" value={symptoms} onChange={(e) => setSymptoms(e.target.value)} className="mb-2" data-testid="vb-cc-symptoms" />
      <Textarea placeholder="Medications (optional)" value={medications} onChange={(e) => setMedications(e.target.value)} className="mb-2" data-testid="vb-cc-meds" />
      <Textarea placeholder="Existing conditions / PMH (optional)" value={existingConditions} onChange={(e) => setExistingConditions(e.target.value)} className="mb-2" data-testid="vb-cc-cond" />
      <Textarea placeholder="Notes / vitals (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-3" data-testid="vb-cc-notes" />
      <Button onClick={submit} disabled={phase === "running"} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="vb-cc-submit">
        {phase === "running" ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</> : "Run Custom Case"}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!v) closeAndReset(); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0" data-testid="vb-custom-dialog">
          <DialogHeader className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
            <DialogTitle className="text-base font-bold text-slate-900">Custom Case Output</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {age} {sex} · {chiefComplaint?.slice(0, 80)}{chiefComplaint?.length > 80 ? "…" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="px-6 py-5">
            {phase === "running" && (
              <div className="flex items-center gap-2 text-sm text-slate-600 py-12 justify-center">
                <Loader2 className="w-4 h-4 animate-spin" />Running deterministic pipeline…
              </div>
            )}
            {phase === "error" && <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">{errMsg}</div>}
            {phase === "done" && result && <ClinicalOutput result={result} viewMode="physician" />}
          </div>
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-3 flex justify-end">
            <Button variant="outline" onClick={closeAndReset} size="sm" data-testid="vb-custom-close">
              <X className="w-3.5 h-3.5 mr-1.5" />Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────
export default function ValidatorBankPage() {
  const [cases, setCases] = useState([]);
  const [openCase, setOpenCase] = useState(null);

  useEffect(() => {
    axios.get(`${API}/test-bank/cases`).then(res => setCases(res.data?.cases || []));
  }, []);

  const openSoap = useCallback((c) => setOpenCase(c), []);
  const closeSoap = useCallback(() => setOpenCase(null), []);

  const peds = cases.filter(c => _bucketOf(c.specialty) === "PEDS");
  const adult = cases.filter(c => _bucketOf(c.specialty) === "ADULT");
  const multi = cases.filter(c => _bucketOf(c.specialty) === "MULTI");

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-6" data-testid="validator-bank-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-4">
          <h1 className="text-2xl font-bold text-slate-900">Clinical Validator Bank</h1>
          <p className="text-sm text-slate-600 mt-1">
            For invited clinical evaluators · All cases are fictional · No PHI
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {cases.length} pre-curated case{cases.length === 1 ? "" : "s"} across pediatric, adult, and multi-domain edge presentations.
            Click <strong>View SOAP</strong> on any row to see the case information and the full deterministic clinical output.
          </p>
        </div>

        <CustomCasePanel />

        <h2 className="font-bold text-slate-900 text-sm mb-2 mt-4">Pediatric Cases ({peds.length})</h2>
        <div className="space-y-2 mb-6">
          {peds.map(c => <CaseRow key={c.case_id} c={c} onOpen={openSoap} />)}
        </div>

        <h2 className="font-bold text-slate-900 text-sm mb-2">Adult Cases ({adult.length})</h2>
        <div className="space-y-2 mb-6">
          {adult.map(c => <CaseRow key={c.case_id} c={c} onOpen={openSoap} />)}
        </div>

        <h2 className="font-bold text-slate-900 text-sm mb-2">Multi-Domain Edge Cases ({multi.length})</h2>
        <div className="space-y-2 mb-6">
          {multi.map(c => <CaseRow key={c.case_id} c={c} onOpen={openSoap} />)}
        </div>
      </div>

      <CaseSoapDialog open={!!openCase} onClose={closeSoap} caseObj={openCase} />
    </div>
  );
}
